/**
 * PSTUDIO-3897.
 *
 * Channel mutations are fire-and-forget events: the form hands the machine a
 * `cb` / `errorCb` pair and waits to be called back. Nothing guaranteed exactly
 * one of them ever fired — `onSubmit` didn't even pass an `errorCb`, so a save
 * the machine discarded produced no mutation, no toast and no navigation.
 *
 * This wraps that handoff so the outcome is always exactly one of success,
 * failure, or timeout — whichever happens first. The timeout is the backstop for
 * a path that settles neither (the reason this bug was invisible).
 */
export interface SaveSettlementOptions {
  onSuccess: () => void;
  onFailure: () => void;
  /** Backstop only; a healthy save settles long before this. */
  timeoutMs?: number;
}

export interface SaveSettlement {
  cb: () => void;
  errorCb: () => void;
  /** Drop the timer without settling — for unmount. */
  cancel: () => void;
}

export const DEFAULT_SAVE_SETTLEMENT_TIMEOUT_MS = 45_000;

export const createSaveSettlement = ({
  onSuccess,
  onFailure,
  timeoutMs = DEFAULT_SAVE_SETTLEMENT_TIMEOUT_MS,
}: SaveSettlementOptions): SaveSettlement => {
  let settled = false;

  const settle = (handler: () => void) => () => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    handler();
  };

  const timer = setTimeout(() => settle(onFailure)(), timeoutMs);

  return {
    cb: settle(onSuccess),
    errorCb: settle(onFailure),
    cancel: () => {
      settled = true;
      clearTimeout(timer);
    },
  };
};
